// apps/office/src/components/Common/ChatBox.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Paperclip, X, User, Loader2, AlertCircle } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const ChatBox = ({ leadId, taskId, projectId, height = '400px' }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Build filter
  const getFilter = () => {
    if (leadId) return `leadId = "${leadId}"`;
    if (taskId) return `taskId = "${taskId}"`;
    if (projectId) return `projectId = "${projectId}"`;
    return null;
  };

  const filter = getFilter();

  // Fetch messages with error handling
  const { data: messages = [], isLoading, error, refetch } = useQuery({
    queryKey: ['chatMessages', filter],
    queryFn: async () => {
      if (!filter) return [];
      try {
        return await pb.collection('chat_messages').getFullList({
          sort: 'created',
          filter,
          expand: 'sender',
        });
      } catch (err) {
        // If collection not found, return empty array and don't retry
        if (err.status === 404) {
          console.warn('Chat collection not found. Please create "chat_messages" collection in PocketBase.');
          return [];
        }
        throw err;
      }
    },
    enabled: !!filter,
    refetchInterval: 5000,
    retry: (failureCount, error) => {
      // Don't retry on 404
      if (error?.status === 404) return false;
      return failureCount < 2;
    },
  });

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const sendMessage = useMutation({
    mutationFn: async () => {
      const data = {
        sender: user.id,
        message: message.trim() || undefined,
      };
      if (leadId) data.leadId = leadId;
      if (taskId) data.taskId = taskId;
      if (projectId) data.projectId = projectId;

      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined) formData.append(key, data[key]);
      });
      for (const file of files) {
        formData.append('files', file);
      }

      return pb.collection('chat_messages').create(formData);
    },
    onSuccess: () => {
      setMessage('');
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries(['chatMessages', filter]);
    },
    onError: (err) => {
      if (err.status === 404) {
        toast.error('Chat system not set up. Please contact admin.');
      } else {
        toast.error('Failed to send: ' + err.message);
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim() && files.length === 0) {
      toast.error('Please type a message or attach a file');
      return;
    }
    sendMessage.mutate();
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selected]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getFileUrl = (record, filename) => {
    try {
      return pb.files.getUrl(record, filename);
    } catch {
      return '#';
    }
  };

  // If collection not found (404), show a friendly message
  if (error?.status === 404) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-slate-400 border border-slate-200 rounded-xl bg-slate-50" style={{ height, minHeight: '200px' }}>
        <AlertCircle className="w-12 h-12 text-amber-400 mb-3" />
        <p className="text-sm font-medium text-slate-600">Chat is not configured yet.</p>
        <p className="text-xs text-slate-400">Please create the "chat_messages" collection in PocketBase.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8" style={{ height, minHeight: '200px' }}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <p className="text-sm text-slate-400 mt-2">Loading messages...</p>
      </div>
    );
  }

  // If any other error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-red-400 border border-red-200 rounded-xl bg-red-50" style={{ height, minHeight: '200px' }}>
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="text-sm font-medium text-red-600">Failed to load messages.</p>
        <button onClick={() => refetch()} className="mt-2 text-sm text-indigo-600 hover:underline">
          Retry
        </button>
      </div>
    );
  }

  // Normal render
  return (
    <div className="flex flex-col border border-slate-200 rounded-xl bg-white overflow-hidden">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: height, minHeight: '200px' }}>
        {messages.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-6">No messages yet. Start the conversation.</div>
        ) : (
          messages.map((msg) => {
            const sender = msg.expand?.sender;
            const isMine = sender?.id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-xl px-4 py-2 ${isMine ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-800'}`}>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    {sender && (
                      <>
                        <span className="font-semibold">{sender.name || sender.username}</span>
                        <span className="text-slate-400">({sender.role || 'user'})</span>
                      </>
                    )}
                    <span className="text-slate-400">• {new Date(msg.created).toLocaleTimeString()}</span>
                  </div>
                  {msg.message && <p className="text-sm whitespace-pre-wrap">{msg.message}</p>}
                  {msg.files && msg.files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {msg.files.map((file, idx) => {
                        const url = getFileUrl(msg, file);
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
                        return (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-white/20 rounded-lg p-1 hover:opacity-80 transition"
                          >
                            {isImage ? (
                              <img src={url} alt="attachment" className="w-20 h-20 object-cover rounded" />
                            ) : (
                              <div className="flex items-center gap-1 bg-slate-200 px-2 py-1 rounded text-xs">
                                <Paperclip className="w-3 h-3" /> {file}
                              </div>
                            )}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-3 bg-slate-50">
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {files.map((file, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 bg-slate-200 text-slate-700 px-2 py-1 rounded text-sm">
                {file.name}
                <button onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
          />
          <label className="cursor-pointer p-2 hover:bg-slate-200 rounded-lg transition">
            <Paperclip className="w-5 h-5 text-slate-500" />
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.zip,.rar,.dwg,.dxf"
            />
          </label>
          <button
            type="submit"
            disabled={sending}
            className="p-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatBox;